import React from 'react';
import { getCityAllCourtTagVenueUrlWithSport } from 'constants/pages';
import { VenueAccessTypesEnum } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import Link from 'components/Link';
import { Props } from './types';
import { generateFaqs } from './utils';

const MaybeTagSlugLink = ({
  citySlug,
  tagSlug,
  children,
  count,
  sport,
}: {
  citySlug: string;
  tagSlug: string;
  children: React.ReactNode;
  count: number;
  sport?: string;
}) => {
  if (count > 0) {
    const href = getCityAllCourtTagVenueUrlWithSport(citySlug, tagSlug, sport);
    return <Link href={href}>{children}</Link>;
  } else {
    return <span>{children}</span>;
  }
};

const courtAndCoachText = ({
  courtCount,
  coachCount,
  cityName,
  sport,
}: {
  courtCount: number;
  coachCount: number;
  cityName: string;
  sport?: string;
}) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  const sportNameLower = sportName.toLowerCase();

  if (courtCount === 0 && coachCount === 0) {
    return `There are no ${sportNameLower} courts or coaches in ${cityName}.`;
  } else if (courtCount === 0) {
    return `There are no ${sportNameLower} courts in ${cityName}, but you can find one of the ${coachCount} ${sportNameLower} coaches in ${cityName}.`;
  } else if (coachCount === 0) {
    return `You can find one of the ${courtCount} ${sportNameLower} courts in ${cityName}. There are no ${sportNameLower} coaches in ${cityName}, but you can Bounce offers remote ${sportNameLower} lessons online. You can learn from the best, anywhere in the world with remote ${sportNameLower} coaches.`;
  } else {
    return `Play at one of the ${courtCount} ${sportNameLower} ${pluralize({
      count: courtCount,
      singular: 'court',
      plural: 'courts',
      hideCount: true,
    })} in ${cityName}, and improve your skills with one of the ${coachCount} ${sportNameLower} coaches in ${cityName}.`;
  }
};

const tournamentText = ({
  tournamentCount,
  stateTournamentCount,
  cityName,
  stateName,
  sport,
}: {
  tournamentCount: number;
  stateTournamentCount: number;
  cityName: string;
  stateName: string;
  sport?: string;
}) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  const sportNameLower = sportName.toLowerCase();

  if (tournamentCount === 0 && stateTournamentCount === 0) {
    return `There are no upcoming ${sportNameLower} tournaments in ${cityName} or your state. Create a round robin to see it here!`;
  } else if (tournamentCount === 0) {
    return `Ready to compete? There are no upcoming ${sportNameLower} tournaments in the city of ${cityName}, but ${stateName} has ${pluralize(
      {
        count: stateTournamentCount,
        singular: 'tournament',
        plural: 'tournaments',
      },
    )} you can compete in.`;
  } else {
    return `Ready to compete? Play in one of the ${tournamentCount} upcoming ${sportNameLower} tournaments in ${cityName}, or play in one of the ${stateTournamentCount} state tournaments in ${stateName}.`;
  }
};

const groupText = ({
  groupCount,
  cityName,
  sport,
}: {
  groupCount: number;
  cityName: string;
  sport?: string;
}) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  const sportNameLower = sportName.toLowerCase();
  return `${sportName} was meant to be played with friends - there are ${groupCount} ${sportNameLower} communities in ${cityName}. Join a group, schedule a round robin, and play!`;
};

export default function CityPageText(
  props: Props & { cityNameFull: string; cityNameShort: string },
) {
  const {
    cityNameFull,
    cityNameShort,
    tournaments,
    city,
    stateTournaments,
    coaches,
    coachStarterProfiles,
    groups,
    sport,
  } = props;
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  const sportNameLower = sportName.toLowerCase();
  const courtsCount = city?.venues?.length || 0;
  const tournamentCount = tournaments?.length || 0;
  const stateTournamentCount = stateTournaments?.length || 0;
  const coachCount = coaches?.length || 0 + coachStarterProfiles?.length || 0;
  const groupCount = groups?.length || 0;
  const freeCourtCount =
    city?.venues?.filter((venue) => venue?.accessType === VenueAccessTypesEnum.Free)?.length || 0;
  const indoorCourtCount = city?.venues?.filter((venue) => venue?.indoorCourtCount)?.length || 0;
  const outdoorCourtCount = city?.venues?.filter((venue) => venue?.outdoorCourtCount)?.length || 0;

  return (
    <div className="px-4">
      <div className="prose mx-auto max-w-7xl border-t border-color-border-input-lightmode pb-10 dark:prose-invert dark:border-color-border-input-darkmode">
        <div>
          <h2>
            Find {sportName} Near You in {cityNameFull}
          </h2>
          <p>
            Find {sportNameLower} lessons, courts, tournaments, and groups in {cityNameFull} using
            the simplest and most comprehensive {sportNameLower} directory.{' '}
          </p>
          <p>
            {courtAndCoachText({
              courtCount: courtsCount,
              coachCount: coachCount,
              cityName: cityNameShort,
              sport,
            })}
          </p>
          <p>
            {tournamentText({
              tournamentCount,
              stateTournamentCount,
              cityName: cityNameShort,
              stateName: city?.countrySubdivision?.name || 'your state',
              sport,
            })}
          </p>
          {groupCount > 0 && groupText({ groupCount, cityName: cityNameShort, sport })}
        </div>
        {courtsCount > 0 && (
          <div>
            <h2>
              Play {sportName} at{' '}
              {pluralize({
                count: courtsCount,
                singular: 'Court',
                plural: 'Courts',
              })}{' '}
              in {cityNameFull}
            </h2>
            <p>
              Finding places to play is the first step. There are {courtsCount} courts near you in
              your {cityNameShort}, and many more in surrounding cities. In {cityNameShort},{' '}
              <MaybeTagSlugLink
                sport={sport}
                citySlug={city?.slug || ''}
                tagSlug="free"
                count={freeCourtCount}
              >
                {pluralize({
                  count: freeCourtCount,
                  singular: 'court',
                  plural: 'courts',
                })}{' '}
                {pluralize({
                  count: freeCourtCount,
                  singular: 'is',
                  plural: 'are',
                  hideCount: true,
                })}{' '}
                free
              </MaybeTagSlugLink>
              ,{' '}
              <MaybeTagSlugLink
                sport={sport}
                citySlug={city?.slug || ''}
                tagSlug="indoor"
                count={indoorCourtCount}
              >
                {pluralize({
                  count: indoorCourtCount,
                  singular: 'location has',
                  plural: 'locations have',
                })}{' '}
                indoor courts
              </MaybeTagSlugLink>
              , and{' '}
              <MaybeTagSlugLink
                sport={sport}
                citySlug={city?.slug || ''}
                tagSlug="outdoor"
                count={outdoorCourtCount}
              >
                {pluralize({
                  count: outdoorCourtCount,
                  singular: 'location has',
                  plural: 'locations have',
                })}{' '}
                outdoor courts
              </MaybeTagSlugLink>
              .
            </p>
          </div>
        )}
        <div>
          <h2>Frequently Asked Questions</h2>
        </div>
        <div>
          {generateFaqs(props).map(({ question, answer }, index) => (
            <div key={index}>
              <h3>{question}</h3>
              <p>{answer}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
